import * as React from "react";
import { ChangeEvent } from "react";
import Typography from "@material-ui/core/Typography/Typography";
import BreadCrumb from "../../shared/components/BreadCrumb";
import store from "../../core/store";
import { loadCommunityMemberships, loadMoreCommunityMemberships, } from "../../core/actions/communitiesActions";
import { connect } from "react-redux";
import ICommunityMembership from "../types/ICommunityMembership";
import { Theme, WithStyles } from "@material-ui/core";
import { RouteComponentProps } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import ICommunityOwnership from "../types/ICommunityOwnership";
import { isAdmin } from "../../core/auth/identityAuth";
import { ILoadCommunityMgmtMembershipsFilter } from "../types/ILoadCommunityMgmtMembershipsFilter";
import { IPagedCommunityMgmtMemberships } from "../types/IPagedCommunityMgmtMemberships";
import { IMembershipsFilter } from "../types/IMembershipsFilter";
import CommunityMembershipsOverviewTable from "./CommunityMembershipsOverviewTable";
import CommunityMembershipsFilter from "./CommunityMembershipsOverviewFilter";

const PAGE_SIZE = 25;

const styles = (theme: Theme) => ({});

interface IState {
  communityMemberships: ICommunityMembership[],
  filter: IMembershipsFilter
}

interface IProps extends WithStyles<typeof styles>, RouteComponentProps<any> {
  communityMemberships: IPagedCommunityMgmtMemberships,
  communityOwnerships: ICommunityOwnership[],
  isLoading: boolean,
  loadMoreIsLoading: boolean,
  currentCommunity: ICommunityOwnership
}

class CommunityMembershipsOverview extends React.Component<IProps, IState> {
  constructor(props: any) {
    super(props);

    const initialStatusFilter = {
      active: false,
      pending: false,
      disabled: false,
      denied: false,
    };

    // Check Pending filter if we come from CommunityHomeManagement home and pending memberships were detected
    if (this.props.location.state) {
      initialStatusFilter.pending = this.props.location.state.pendingMembershipsPresent
    }

    this.state = {
      communityMemberships: [],
      filter: {
        community: this.props.match.params.communityCode,
        entity: null,
        statuses: initialStatusFilter
      }
    };

    if (isAdmin) {
      this.loadCommunityManagementMemberships();
    }

    this.isCommunityAdmin = this.isCommunityAdmin.bind(this);
    this.loadCommunityManagementMemberships = this.loadCommunityManagementMemberships.bind(this);
    this.buildApiFilter = this.buildApiFilter.bind(this);
    this.onLoadMore = this.onLoadMore.bind(this);
    this.entityNameFilterIsValid = this.entityNameFilterIsValid.bind(this);
    this.onChangeTextFilter = this.onChangeTextFilter.bind(this);
    this.onChangeStatusFilter = this.onChangeStatusFilter.bind(this);
  }

  public isCommunityAdmin() {
    return this.props.communityOwnerships && this.props.communityOwnerships.length > 0
  }

  public getSelectedStatusesList(statuses: {
    active: boolean,
    pending: boolean,
    disabled: boolean,
    denied: boolean,
  }): string[] {
    return Object.entries(statuses).filter(([_, value]) => value).map(([key, value]) => key);
  }

  public buildApiFilter(page: number): ILoadCommunityMgmtMembershipsFilter {
    return {
      communityCode: this.state.filter.community,
      entityName: this.state.filter.entity,
      statuses: this.getSelectedStatusesList(this.state.filter.statuses),
      pageSize: PAGE_SIZE,
      page
    };
  }

  public loadCommunityManagementMemberships() {
    const filter: ILoadCommunityMgmtMembershipsFilter = this.buildApiFilter(1)
    store.dispatch(loadCommunityMemberships(filter));
  }

  public onLoadMore() {
    const filter: ILoadCommunityMgmtMembershipsFilter = this.buildApiFilter(this.props.communityMemberships._page.page + 1);
    store.dispatch(loadMoreCommunityMemberships(filter));
  }

  public onChangeStatusFilter(status: string, checked: boolean) {
    const updatedStatusFilter = this.state.filter.statuses;
    updatedStatusFilter[status] = checked;

    this.setState({
      filter: {
        ...this.state.filter,
        statuses: updatedStatusFilter,
      }
    }, this.reloadDataAfterFilterChange)
  }

  public reloadDataAfterFilterChange() {
    if (this.entityNameFilterIsValid()) {
      this.loadCommunityManagementMemberships()
    }
  }

  public onChangeTextFilter(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    const name = e.target.name;
    const value = e.target.value;

    this.setState({
      filter: {
        ...this.state.filter,
        [name]: value
      }
    }, this.reloadDataAfterFilterChange)
  }

  public entityNameFilterIsValid() {
    if (this.state.filter.entity) {
      return this.state.filter.entity.length > 3;
    }
    return true;
  }

  public canLoadMore(): boolean {
    if (this.props.communityMemberships) {
      return this.props.communityMemberships.canLoadMore;
    }
    return false;
  }

  public render() {
    const currentCommunity = this.props.communityOwnerships.find((ownership: ICommunityOwnership) => ownership.communityCode === this.props.match.params.communityCode);
    return (
      <div>
        <Typography variant="title">{currentCommunity ? `${currentCommunity.communityName} | ` : ''}  Community memberships overview</Typography>
        <Typography color="textSecondary" gutterBottom>
          <BreadCrumb value="Console" to="/"/>
          <BreadCrumb value="Community management" to="/community-management"/>
          Memberships overview
        </Typography>

        <CommunityMembershipsFilter
          disabled={!this.isCommunityAdmin()}
          filter={this.state.filter}
          onChangeStatusFilter={this.onChangeStatusFilter}
          onChangeTextFilter={this.onChangeTextFilter}
          entityNameFilterIsValid={this.entityNameFilterIsValid}
        />

        <CommunityMembershipsOverviewTable
          communityOwnerships={this.props.communityOwnerships}
          communityMemberships={this.props.communityMemberships}
          isLoading={this.props.isLoading}
          loadMoreIsLoading={this.props.loadMoreIsLoading}
          canLoadMore={this.canLoadMore()}
          onLoadMore={this.onLoadMore}
          isCommunityAdmin={this.isCommunityAdmin}
        />
      </div>
    );
  }
}

function mapStateToProps(state: any) {
  return {
    communityOwnerships: state.communitiesReducer.communityOwnerships,
    communityMemberships: state.communitiesReducer.communityMemberships ? state.communitiesReducer.communityMemberships : [],
    isLoading: state.communitiesReducer.communityOwnershipsLoading || state.communitiesReducer.communityManagementMembershipsLoading,
    loadMoreIsLoading: state.communitiesReducer.moreCommunityManagementMembershipsLoading
  };
}

export default connect(mapStateToProps)(withStyles(styles)(CommunityMembershipsOverview))




// WEBPACK FOOTER //
// ./src/modules/communities/components/CommunityMembershipsOverview.tsx